import React, { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { entferneAn, ersetzeAn, fuegeHinzu } from './gbuModulLogic';
import { useUngespeichert } from './useUngespeichert';

interface Props {
  werte: string[];
  onChange: (werte: string[]) => void;
  disabled?: boolean;
  platzhalter?: string;
  /** Kurze Eintraege als Chips (Vorschriften), lange als Zeilen (Abschnitt 5). */
  art?: 'chips' | 'zeilen';
  leerText?: string;
}

/**
 * Eine Liste von Texten bearbeiten — Vorschriften (Chips) oder die
 * organisatorischen Massnahmen aus Abschnitt 5 (Zeilen). Hinzufuegen mit
 * Enter oder Plus; Doppeltes und Leeres faellt beim Hinzufuegen weg.
 */
export const ListenEditor: React.FC<Props> = ({ werte, onChange, disabled = false, platzhalter = 'Eintrag hinzufügen', art = 'chips', leerText = 'Noch kein Eintrag.' }) => {
  const [neu, setNeu] = useState('');
  // Ein eingetippter, noch nicht hinzugefuegter Eintrag ist eine ungespeicherte Eingabe (sperrt „Zur Freigabe", fragt beim Verlassen).
  useUngespeichert(!disabled && neu.trim() !== '');

  const hinzu = () => {
    if (!neu.trim()) return;
    onChange(fuegeHinzu(werte, neu));
    setNeu('');
  };

  return (
    <div className="space-y-2">
      {werte.length === 0 && disabled && <p className="text-xs text-slate-400 italic">{leerText}</p>}
      {art === 'chips' ? (
        werte.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {werte.map((w, i) => (
              <span key={`${w}-${i}`} className="inline-flex items-center gap-1 max-w-full px-2 py-1 text-xs font-semibold text-slate-700 bg-slate-100 border border-slate-200 rounded-lg">
                <span className="break-words min-w-0">{w}</span>
                {!disabled && (
                  <button type="button" onClick={() => onChange(entferneAn(werte, i))} className="p-0.5 hover:bg-slate-200 rounded" title="Entfernen">
                    <X className="w-3 h-3" />
                  </button>
                )}
              </span>
            ))}
          </div>
        )
      ) : (
        <ul className="space-y-1.5">
          {werte.map((w, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="text-xs text-slate-400 font-bold pt-2.5 w-5 text-right shrink-0">{i + 1}.</span>
              <textarea
                value={w}
                disabled={disabled}
                onChange={(e) => onChange(ersetzeAn(werte, i, e.target.value))}
                rows={1}
                className="flex-1 min-w-0 px-3 py-2 min-h-[40px] text-sm text-slate-800 bg-slate-50 rounded-lg outline-none focus:bg-white focus:ring-1 focus:ring-amber-300 disabled:text-slate-600 resize-y"
              />
              {!disabled && (
                <button type="button" onClick={() => onChange(entferneAn(werte, i))} className="p-2 min-h-[40px] text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg" title="Zeile entfernen">
                  <X className="w-4 h-4" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {!disabled && (
        <div className="flex gap-2">
          <input
            value={neu}
            onChange={(e) => setNeu(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); hinzu(); } }}
            onBlur={() => { if (neu.trim()) hinzu(); }}
            placeholder={platzhalter}
            className="flex-1 min-w-0 px-3 py-2 min-h-[40px] text-sm text-slate-800 bg-slate-50 rounded-lg outline-none focus:bg-white focus:ring-1 focus:ring-amber-300"
          />
          <button type="button" onClick={hinzu} disabled={!neu.trim()} className="min-h-[40px] px-3 flex items-center gap-1 text-xs font-bold text-amber-800 bg-amber-50 hover:bg-amber-100 rounded-lg disabled:opacity-40">
            <Plus className="w-3.5 h-3.5" /> Hinzufügen
          </button>
        </div>
      )}
      {!disabled && neu.trim() !== '' && (
        <p className="text-[11px] font-bold text-amber-700">Noch nicht übernommen — Enter, „Hinzufügen" oder Feld verlassen.</p>
      )}
    </div>
  );
};
